import React, { useMemo, useState } from 'react';
import { FiEdit2, FiExternalLink } from 'react-icons/fi';
import { StatementsAPI } from '../services/api';
import { LoaderInline, Spinner } from './Loader';

const colors = {
  stone100: '#f5f5f4',
  stone200: '#e7e5e4',
  stone500: '#78716c',
  stone700: '#44403c',
  stone800: '#292524',
  greenBg: '#ecfdf5',
  greenFg: '#065f46',
  yellowBg: '#fef3c7',
  yellowFg: '#92400e',
  greyBg: '#f3f4f6',
  greyFg: '#374151'
};

function amountOf(txn) {
  const d = Number(txn?.debit || 0);
  if (d > 0) return d;
  return Number(txn?.credit || 0);
}

function currencyCode(v, fallback = 'INR') {
  const s = String(v || '').trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(s)) return s;
  return fallback;
}

function bucket(txn) {
  const status = String(txn?.matchStatus || '').toUpperCase();
  const conf = Number(txn?.matchConfidence || 0);
  if (status === 'CONFIRMED' || conf >= 0.9) return 'high';
  if (status === 'SUGGESTED' || conf >= 0.7) return 'suggested';
  return 'unmatched';
}

function normalizeName(v) {
  return String(v || '').trim().toLowerCase();
}

export default function MatchReviewPanel({
  statement,
  statementById = {},
  expenseOptions = [],
  onUpdated,
  onViewExpense,
  companyName = '',
  matchFilter: matchFilterProp,
  onMatchFilterChange,
  onRerunMatching
}) {
  const [busyTxnId, setBusyTxnId] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [manualExpenseIdByTxn, setManualExpenseIdByTxn] = useState({});
  const [paidByNameByTxn, setPaidByNameByTxn] = useState({});
  const [reimbByTxn, setReimbByTxn] = useState({});
  const [statementPanelOpen, setStatementPanelOpen] = useState(false);
  const [activeFileUrl, setActiveFileUrl] = useState('');
  const [activeStatementTitle, setActiveStatementTitle] = useState('');
  const [matchFilterInternal, setMatchFilterInternal] = useState('ALL');
  const [actionsOpenByTxn, setActionsOpenByTxn] = useState({});
  const matchFilter = matchFilterProp !== undefined ? matchFilterProp : matchFilterInternal;
  const setMatchFilter = onMatchFilterChange || setMatchFilterInternal;

  const txns = Array.isArray(statement?.transactions) ? statement.transactions : [];
  const sorted = useMemo(() => [...txns].sort((a, b) => {
    const ad = String(a?.date || '');
    const bd = String(b?.date || '');
    if (ad !== bd) return ad.localeCompare(bd);
    return Number(a?.rowIndex || 0) - Number(b?.rowIndex || 0);
  }), [txns]);

  const filteredRows = useMemo(() => {
    if (matchFilter === 'ALL') return sorted;
    return sorted.filter((t) => {
      const status = String(t?.matchStatus || '').toUpperCase();
      const isMatched = bucket(t) !== 'unmatched';
      if (matchFilter === 'MATCHED') return isMatched;
      if (matchFilter === 'UNMATCHED') return !isMatched;
      if (matchFilter === 'REJECTED') return status === 'REJECTED';
      if (matchFilter === 'CONFIRMED') return status === 'CONFIRMED';
      return true;
    });
  }, [sorted, matchFilter]);

  const filterStats = useMemo(() => {
    const counts = {
      ALL: sorted.length,
      MATCHED: 0,
      UNMATCHED: 0,
      REJECTED: 0,
      CONFIRMED: 0
    };
    for (const t of sorted) {
      const status = String(t?.matchStatus || '').toUpperCase();
      const isMatched = bucket(t) !== 'unmatched';
      if (isMatched) counts.MATCHED += 1;
      else counts.UNMATCHED += 1;
      if (status === 'REJECTED') counts.REJECTED += 1;
      if (status === 'CONFIRMED') counts.CONFIRMED += 1;
    }
    return counts;
  }, [sorted]);

  const confirmTxn = async (txn, overrideExpenseId, paidByName, isReimbursement) => {
    const txnId = Number(txn?.id || 0);
    if (!txnId) return;
    setBusyTxnId(txnId);
    try {
      const payload = {};
      if (overrideExpenseId) payload.expenseId = Number(overrideExpenseId);
      payload.paidByName = String(paidByName || '').trim();
      payload.isReimbursement = Boolean(isReimbursement);
      await StatementsAPI.confirmMatch(txnId, payload);
      const stmtId = Number(txn?.statementId || statement?.id || 0);
      if (onUpdated && stmtId) {
        const refreshed = await StatementsAPI.getById(stmtId);
        onUpdated(refreshed);
      }
    } catch (err) {
      window.alert(err?.response?.data?.error || err.message || 'Confirm failed');
    }
    setBusyTxnId(null);
  };

  const rejectTxn = async (txn) => {
    const txnId = Number(txn?.id || 0);
    if (!txnId) return;
    setBusyTxnId(txnId);
    try {
      await StatementsAPI.rejectMatch(txnId);
      const stmtId = Number(txn?.statementId || statement?.id || 0);
      if (onUpdated && stmtId) {
        const refreshed = await StatementsAPI.getById(stmtId);
        onUpdated(refreshed);
      }
    } catch (err) {
      window.alert(err?.response?.data?.error || err.message || 'Reject failed');
    }
    setBusyTxnId(null);
  };

  const confirmAllHigh = async () => {
    const high = sorted.filter((t) => bucket(t) === 'high' && String(t?.matchStatus || '').toUpperCase() !== 'CONFIRMED');
    for (const t of high) {
      // eslint-disable-next-line no-await-in-loop
      await confirmTxn(t);
    }
  };

  const rerunMatching = async () => {
    if (rerunning) return;
    setRerunning(true);
    try {
      if (onRerunMatching) {
        await onRerunMatching();
      } else if (statement?.id) {
        const res = await StatementsAPI.runMatch(statement.id);
        const updated = res?.statement;
        if (onUpdated && updated) onUpdated(updated);
        else if (onUpdated) {
          const refreshed = await StatementsAPI.getById(statement.id);
          onUpdated(refreshed);
        }
      }
    } catch (err) {
      window.alert(err?.response?.data?.error || err.message || 'Re-run matching failed');
    }
    setRerunning(false);
  };

  return (
    <div style={{ border: `1px solid ${colors.stone200}`, borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: 12, borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, color: colors.stone800 }}>Match Review</div>
          <div style={{ fontSize: 12, color: colors.stone500 }}>{statement?.name || `Statement #${statement?.id || ''}`}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {['ALL', 'MATCHED', 'UNMATCHED', 'REJECTED', 'CONFIRMED'].map((key) => {
              const active = matchFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMatchFilter(key)}
                  title={`Show ${key.toLowerCase()} transactions`}
                  style={{
                    border: `1px solid ${active ? colors.stone700 : colors.stone200}`,
                    borderRadius: 10,
                    padding: '4px 9px',
                    fontSize: 11,
                    fontWeight: 300,
                    background: active ? colors.stone800 : '#fff',
                    color: active ? '#fff' : colors.stone700,
                    cursor: 'pointer'
                  }}
                >
                  {key} ({filterStats[key] || 0})
                </button>
              );
            })}
          </div>
          <button className="btn btn-secondary" onClick={confirmAllHigh} disabled={Boolean(busyTxnId)}>
            {busyTxnId ? <LoaderInline label="Saving" /> : 'Confirm All High-Confidence'}
          </button>
          <button className="btn btn-secondary" onClick={rerunMatching} disabled={rerunning || Boolean(busyTxnId) || (!onRerunMatching && !statement?.id)}>
            {rerunning ? <LoaderInline label="Re-running" /> : 'Re-run Matching'}
          </button>
        </div>
      </div>

      <div style={{ maxHeight: 520, overflow: 'auto' }}>
        {filteredRows.length === 0 ? (
          <div style={{ padding: 16, color: colors.stone500, fontSize: 13 }}>No transactions found.</div>
        ) : filteredRows.map((txn) => {
          const type = bucket(txn);
          const bg = type === 'high' ? colors.greenBg : (type === 'suggested' ? colors.yellowBg : colors.greyBg);
          const fg = type === 'high' ? colors.greenFg : (type === 'suggested' ? colors.yellowFg : colors.greyFg);
          const ocrPayeeName = String(txn?.rawRow?.payeeName || '').trim();
          const ocrPaymentMethodRaw = String(txn?.rawRow?.paymentMethod || '').trim().toUpperCase();
          const ocrPaymentMethod = ocrPaymentMethodRaw ? ocrPaymentMethodRaw.replace(/_/g, ' ') : '';
          const txnCurrency = currencyCode(txn?.currency || txn?.rawRow?.currency || null, 'INR');
          const matchedExpense = txn?.matchedExpense || null;
          const fallbackExpense = !matchedExpense && txn?.matchedExpenseId
            ? (expenseOptions.find((e) => Number(e?.id) === Number(txn.matchedExpenseId)) || null)
            : null;
          const displayExpense = matchedExpense || fallbackExpense;
          const confidencePct = Number((Number(txn.matchConfidence || 0) * 100).toFixed(0));
          const manualValue = manualExpenseIdByTxn[txn.id] || '';
          const selectedExpenseId = manualValue ? Number(manualValue) : Number(displayExpense?.id || 0);
          const selectedExpense = selectedExpenseId
            ? (expenseOptions.find((e) => Number(e?.id) === selectedExpenseId) || displayExpense || null)
            : displayExpense;
          const latestReimbursement = (Array.isArray(selectedExpense?.reimbursements) ? selectedExpense.reimbursements : [])
            .filter((row) => String(row?.status || '').toLowerCase() !== 'voided')
            .sort((a, b) => Number(b?.sequenceNo || 0) - Number(a?.sequenceNo || 0) || Number(b?.id || 0) - Number(a?.id || 0))[0];
          const defaultPaidByName = String(
            latestReimbursement?.paidToNameSnapshot
            || selectedExpense?.payeeName
            || statement?.accountHolderName
            || ''
          ).trim();
          const currentPaidByName = Object.prototype.hasOwnProperty.call(paidByNameByTxn, txn.id)
            ? String(paidByNameByTxn[txn.id] || '')
            : defaultPaidByName;
          const trimmedPaidByName = String(currentPaidByName || '').trim();
          const hasMismatch = Boolean(trimmedPaidByName)
            && Boolean(normalizeName(companyName))
            && normalizeName(trimmedPaidByName) !== normalizeName(companyName);
          const isReimbursementChecked = Object.prototype.hasOwnProperty.call(reimbByTxn, txn.id)
            ? Boolean(reimbByTxn[txn.id])
            : hasMismatch;
          const status = String(txn?.matchStatus || '').toUpperCase();
          const isFinalized = status === 'CONFIRMED' || status === 'REJECTED';
          const actionsOpen = !isFinalized || Boolean(actionsOpenByTxn[txn.id]);

          return (
            <div key={txn.id} style={{ borderTop: `1px solid ${colors.stone200}`, padding: 10, background: bg }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 10 }}>
                <div style={{ background: '#fff', borderRadius: 8, padding: 8, border: `1px solid ${colors.stone200}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: colors.stone500, textTransform: 'uppercase' }}>Transaction</div>
                    {(() => {
                      const stmtId = Number(txn?.statementId || 0);
                      const stmt = stmtId ? statementById?.[stmtId] : null;
                      const fileUrl = String(stmt?.fileUrl || statement?.fileUrl || '').trim();
                      if (!fileUrl) return null;
                      const title = stmt?.name || (stmt?.id ? `Statement #${stmt.id}` : (statement?.name || 'Statement'));
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveFileUrl(fileUrl);
                            setActiveStatementTitle(title);
                            setStatementPanelOpen(true);
                          }}
                          title="View statement"
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: colors.stone700, display: 'inline-flex', alignItems: 'center', padding: 2 }}
                        >
                          <FiExternalLink size={14} />
                        </button>
                      );
                    })()}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: colors.stone800 }}>{txn.date || '-'} • {txnCurrency} {amountOf(txn).toFixed(2)}</div>
                  <div style={{ marginTop: 4, fontSize: 12, color: colors.stone700 }}>{txn.description || '-'}</div>
                  <div style={{ marginTop: 2, fontSize: 11, color: colors.stone500 }}>Ref: {txn.reference || '-'} | Row: {txn.rowIndex || '-'}</div>
                  {(() => {
                    const stmtId = Number(txn?.statementId || 0);
                    const stmt = stmtId ? statementById?.[stmtId] : null;
                    const bankName = String(stmt?.bankName || '').trim() || '-';
                    const acct = String(stmt?.accountNumber || '').trim();
                    const last4 = acct ? acct.slice(-4) : '';
                    const method = ocrPaymentMethod || '-';
                    const normStmtName = String(stmt?.name || '').trim().toLowerCase();
                    const normCompany = String(companyName || '').trim().toLowerCase();
                    const kind = (normStmtName && normCompany && normStmtName === normCompany) ? 'Business' : 'Personal';
                    return (
                      <div style={{ marginTop: 2, fontSize: 11, color: colors.stone500 }}>
                        {bankName} {last4 ? `****${last4}` : '-'} - {method} {kind}
                      </div>
                    );
                  })()}
                </div>

                <div style={{ background: '#fff', borderRadius: 8, padding: 8, border: `1px solid ${colors.stone200}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: colors.stone500, textTransform: 'uppercase' }}>Matched Expense</div>
                    {displayExpense?.id && (
                      <button
                        type="button"
                        onClick={() => onViewExpense?.(displayExpense)}
                        title={`View Inv #${displayExpense.number || displayExpense.id}`}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: colors.stone700, display: 'inline-flex', alignItems: 'center', padding: 2 }}
                      >
                        <FiExternalLink size={14} />
                      </button>
                    )}
                  </div>
                  {(displayExpense && confidencePct > 0) ? (
                    <>
                      {(() => {
                        const expCurrency = currencyCode(displayExpense?.currency || 'INR', 'INR');
                        const expTotal = Number(displayExpense?.total || 0).toFixed(2);
                        return (
                          <div style={{ marginTop: 4, fontSize: 13, color: colors.stone800 }}>
                            Inv #{displayExpense.number || displayExpense.id} • {expCurrency} {expTotal}
                          </div>
                        );
                      })()}
                      {(() => {
                        const expCurr = String(displayExpense?.currency || 'INR').toUpperCase();
                        if (expCurr === 'INR') return null;
                        const storedConverted = Number(displayExpense?.convertedAmount || 0);
                        const txnInr = Number(txn?.amountInr || 0);
                        const fallbackInr = txnInr > 0
                          ? txnInr
                          : (txnCurrency === 'INR' ? Number(amountOf(txn) || 0) : 0);
                        const inrValue = storedConverted > 0 ? storedConverted : fallbackInr;
                        return (
                          <div style={{ marginTop: 4, fontSize: 13, color: colors.stone800 }}>
                            {`Converted INR: ${Number(inrValue || 0).toFixed(2)}`}
                          </div>
                        );
                      })()}
                      <div style={{ marginTop: 2, fontSize: 11, color: colors.stone500 }}>
                        Date: {displayExpense.date || '-'}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: colors.stone700 }}>{displayExpense.vendorName || displayExpense.description || '-'}</div>
                    </>
                  ) : (
                    <div style={{ marginTop: 4, fontSize: 12, color: colors.stone500 }}>
                      {confidencePct > 0 ? 'Matched expense not loaded' : 'No match suggested'}
                    </div>
                  )}
                  <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: fg }}>
                    Status: {txn.matchStatus || 'UNMATCHED'} • Confidence: {confidencePct}%
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {isFinalized && !actionsOpen && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    title="Edit match actions"
                    onClick={() => setActionsOpenByTxn((prev) => ({ ...prev, [txn.id]: true }))}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, padding: 0 }}
                  >
                    <FiEdit2 size={15} />
                  </button>
                )}

                {actionsOpen && (
                  <>
                    <select
                      value={manualValue}
                      onChange={(e) => {
                        const nextExpenseId = e.target.value;
                        setManualExpenseIdByTxn((prev) => ({ ...prev, [txn.id]: nextExpenseId }));
                      }}
                      style={{ minWidth: 280, padding: '6px 8px', borderRadius: 8, border: `1px solid ${colors.stone200}` }}
                      disabled={busyTxnId === txn.id}
                    >
                      <option value="">Change match (optional)</option>
                      {expenseOptions.map((e) => (
                        <option key={e.id} value={e.id}>
                          Inv #{e.number || e.id} • {e.date || '-'} • {e.vendorName || e.description || '-'} • {currencyCode(e.currency || 'INR', 'INR')} {Number(e.total || 0).toFixed(2)}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={currentPaidByName}
                      onChange={(e) => setPaidByNameByTxn((prev) => ({ ...prev, [txn.id]: e.target.value }))}
                      placeholder="Paid By (Name)"
                      style={{ minWidth: 220, padding: '6px 8px', borderRadius: 8, border: `1px solid ${colors.stone200}` }}
                      disabled={busyTxnId === txn.id}
                    />
                    {hasMismatch && (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.stone700 }}>
                        <input
                          type="checkbox"
                          checked={isReimbursementChecked}
                          onChange={(e) => setReimbByTxn((prev) => ({ ...prev, [txn.id]: e.target.checked }))}
                          disabled={busyTxnId === txn.id}
                        />
                        Mark as reimbursement
                      </label>
                    )}

                    <button className="btn btn-secondary" onClick={() => rejectTxn(txn)} disabled={busyTxnId === txn.id}>
                      {busyTxnId === txn.id ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Spinner size={13} /> Saving</span> : 'Reject'}
                    </button>
                    <button
                      className="submit-button"
                      onClick={() => confirmTxn(txn, manualValue, currentPaidByName, hasMismatch ? isReimbursementChecked : false)}
                      disabled={busyTxnId === txn.id || (!displayExpense && !manualValue)}
                    >
                      {busyTxnId === txn.id ? <LoaderInline label="Saving" color="#fff" /> : 'Confirm'}
                    </button>
                  </>
                )}

                {txn.proofImageUrl && (
                  <a
                    href={txn.proofImageUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 13, fontWeight: 700, color: colors.stone800, textDecoration: 'underline' }}
                  >
                    View Proof
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {statementPanelOpen && activeFileUrl && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1800, background: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'flex-end' }}
          onClick={() => setStatementPanelOpen(false)}
        >
          <div
            style={{ width: '70%', maxWidth: 980, height: '100%', background: '#fff', borderLeft: `1px solid ${colors.stone200}`, display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: 12, borderBottom: `1px solid ${colors.stone200}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700, color: colors.stone800 }}>{activeStatementTitle || 'Statement Preview'}</div>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setStatementPanelOpen(false);
                  setActiveFileUrl('');
                  setActiveStatementTitle('');
                }}
              >
                Close
              </button>
            </div>
            {String(activeFileUrl).toLowerCase().endsWith('.pdf') ? (
              <iframe title="Statement preview" src={activeFileUrl} style={{ flex: 1, border: 'none', width: '100%' }} />
            ) : (
              <img src={activeFileUrl} alt="Statement preview" style={{ width: '100%', height: '100%', objectFit: 'contain', background: colors.stone100 }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
